import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { generationJobTracker } from "../utils/generation/generationJobTracker";
import {
  addTrackedJobId,
  addDismissedCompletionJobId,
  hasDismissedCompletionJobId,
  readDismissedCompletionJobIds,
  readTrackedJobIds,
  removeTrackedJobId,
} from "../utils/generation/generationJobSession";
import { isGenerationJobTerminal } from "../utils/generation/generationJobConstants";
import { GenerationCompletionToast } from "../components/generation/GenerationCompletionToast";
import { runHeadlessLibrarySave } from "../utils/library/runHeadlessLibrarySave";
import { shouldShowGenerationCompletionToast } from "../utils/generation/shouldShowGenerationCompletionToast";
import {
  subscribeGenerationNavigationGuard,
} from "../lib/generationNavigationGuardStore";
import { useOptionalTemaRoute } from "../routes/TemaRouteContext";

const GenerationJobContext = createContext(null);

function normalizeJob(statusResponse, meta = {}) {
  if (!statusResponse?.jobId) {
    return null;
  }

  const normalized = {
    jobId: statusResponse.jobId,
    status: statusResponse.status,
    phase: statusResponse.phase ?? null,
    progressPercent: statusResponse.progressPercent ?? 0,
    toolId: statusResponse.toolId,
    operation: statusResponse.operation,
    saveToLibrary: statusResponse.saveToLibrary ?? true,
    librarySaved: statusResponse.librarySaved ?? false,
    errorMessage: statusResponse.errorMessage ?? null,
    label: meta.label ?? statusResponse.toolId,
  };

  if (meta.acknowledged !== undefined) {
    normalized.acknowledged = meta.acknowledged;
  }

  if (meta.result !== undefined) {
    normalized.result = meta.result;
  }

  return normalized;
}

export function GenerationJobProvider({ children }) {
  const [jobs, setJobs] = useState({});
  const [completionToast, setCompletionToast] = useState(null);
  const jobsRef = useRef(jobs);
  const foregroundGeneratingToolIdsRef = useRef([]);
  const currentRouteIdRef = useRef(null);
  const dismissedCompletionJobIdsRef = useRef(
    new Set(readDismissedCompletionJobIds()),
  );
  const completionHandlersInFlightRef = useRef(new Set());
  const pollTimerRef = useRef(null);
  const generationCancelHandlersRef = useRef({});

  const isCompletionDismissed = useCallback((jobId) => {
    if (!jobId) {
      return false;
    }
    return (
      dismissedCompletionJobIdsRef.current.has(jobId) ||
      hasDismissedCompletionJobId(jobId)
    );
  }, []);

  const markCompletionDismissed = useCallback((jobId) => {
    if (!jobId) {
      return;
    }
    dismissedCompletionJobIdsRef.current.add(jobId);
    addDismissedCompletionJobId(jobId);
  }, []);
  const routeContext = useOptionalTemaRoute();

  useEffect(() => {
    currentRouteIdRef.current = routeContext?.route?.id ?? null;
  }, [routeContext?.route?.id]);

  useEffect(() => {
    return subscribeGenerationNavigationGuard((state) => {
      const toolIds = state.activeTools
        .filter((tool) => tool.variant === "generating")
        .map((tool) => tool.toolId);
      foregroundGeneratingToolIdsRef.current = toolIds;
    });
  }, []);

  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  const upsertJob = useCallback((statusResponse, meta = {}) => {
    const normalized = normalizeJob(statusResponse, meta);
    if (!normalized) {
      return;
    }
    setJobs((current) => {
      const existing = current[normalized.jobId];
      const next = {
        ...current,
        [normalized.jobId]: {
          ...existing,
          ...normalized,
          label: meta.label ?? existing?.label ?? normalized.label,
          acknowledged:
            meta.acknowledged !== undefined
              ? meta.acknowledged
              : (existing?.acknowledged ?? false),
          result:
            meta.result !== undefined ? meta.result : (existing?.result ?? null),
          request:
            meta.request !== undefined ? meta.request : existing?.request,
        },
      };
      jobsRef.current = next;
      return next;
    });
  }, []);

  const registerJob = useCallback(
    (jobId, meta = {}) => {
      if (!jobId) {
        return;
      }

      const existing = jobsRef.current[jobId];
      const isAcknowledgedTerminal =
        existing?.acknowledged &&
        isGenerationJobTerminal(existing.status ?? "completed");

      if (!isAcknowledgedTerminal) {
        addTrackedJobId(jobId);
      }

      setJobs((current) => {
        const previous = current[jobId];
        const next = {
          ...current,
          [jobId]: {
            jobId,
            status: "queued",
            progressPercent: 0,
            toolId: meta.toolId ?? "unknown-tool",
            operation: meta.operation ?? "generate",
            saveToLibrary: meta.saveToLibrary ?? true,
            librarySaved: false,
            label: meta.label ?? meta.toolId ?? "Content",
            acknowledged: false,
            result: null,
            ...previous,
            ...meta,
          },
        };
        jobsRef.current = next;
        return next;
      });
    },
    [],
  );

  const acknowledgeCompletion = useCallback((jobId) => {
    markCompletionDismissed(jobId);
    setJobs((current) => {
      const job = current[jobId];
      if (!job) {
        return current;
      }
      const next = {
        ...current,
        [jobId]: { ...job, acknowledged: true },
      };
      jobsRef.current = next;
      return next;
    });
    setCompletionToast((current) => (current?.jobId === jobId ? null : current));
    removeTrackedJobId(jobId);
  }, [markCompletionDismissed]);

  const handleJobCompleted = useCallback(
    async (jobId, statusResponse) => {
      if (isCompletionDismissed(jobId)) {
        return;
      }

      if (completionHandlersInFlightRef.current.has(jobId)) {
        return;
      }

      completionHandlersInFlightRef.current.add(jobId);

      try {
        const existingJob = jobsRef.current[jobId];
        if (existingJob?.acknowledged || isCompletionDismissed(jobId)) {
          return;
        }

        let result = existingJob?.result ?? null;
        if (!result) {
          const resultResponse = await generationJobTracker.getResult(jobId);
          result = resultResponse?.result ?? null;
        }

        if (isCompletionDismissed(jobId)) {
          return;
        }

        upsertJob(statusResponse, { result });

        if (!result) {
          return;
        }

        const job = jobsRef.current[jobId];
        if (job?.acknowledged || isCompletionDismissed(jobId)) {
          return;
        }

        if (job?.saveToLibrary && result && !job.librarySaved) {
          await runHeadlessLibrarySave({
            toolId: statusResponse.toolId,
            result,
          });
        }

        if (isCompletionDismissed(jobId) || jobsRef.current[jobId]?.acknowledged) {
          return;
        }

        const showToast = shouldShowGenerationCompletionToast(statusResponse.toolId, {
          foregroundToolIds: foregroundGeneratingToolIdsRef.current,
          currentRouteId: currentRouteIdRef.current,
        });

        if (showToast) {
          if (isCompletionDismissed(jobId) || jobsRef.current[jobId]?.acknowledged) {
            return;
          }
          setCompletionToast({
            jobId,
            toolId: statusResponse.toolId,
            saveToLibrary: statusResponse.saveToLibrary ?? true,
            result,
            request: job?.request ?? null,
          });
        } else {
          acknowledgeCompletion(jobId);
        }
      } finally {
        completionHandlersInFlightRef.current.delete(jobId);
      }
    },
    [acknowledgeCompletion, isCompletionDismissed, upsertJob],
  );

  const pollJobs = useCallback(async () => {
    if (!generationJobTracker.isEnabled()) {
      return;
    }

    const controller = new AbortController();
    const trackedIds = readTrackedJobIds();
    const ids = [...new Set([...trackedIds, ...Object.keys(jobsRef.current)])].filter(
      (jobId) => {
        // Skip jobs we already know are terminal — they never need another poll and
        // would otherwise be re-polled forever (they linger in the jobs state map).
        const known = jobsRef.current[jobId];
        return !(known && isGenerationJobTerminal(known.status));
      },
    );

    if (ids.length === 0) {
      try {
        const active = await generationJobTracker.listActive(controller.signal);
        for (const item of active ?? []) {
          if (isCompletionDismissed(item.jobId)) {
            continue;
          }
          const existing = jobsRef.current[item.jobId];
          if (existing?.acknowledged) {
            continue;
          }
          addTrackedJobId(item.jobId);
          upsertJob(item);
        }
      } catch {
        // ignore list failures
      }
      return;
    }

    for (const jobId of ids) {
      const previous = jobsRef.current[jobId];
      const status = await generationJobTracker.getStatus(jobId, controller.signal);
      if (!status) {
        continue;
      }

      if (status.notFound) {
        // The backend no longer knows this job (e.g. server restart or expiry).
        // Mark it terminal so we stop polling it and any tool waiting on it unwedges.
        if (previous && !isGenerationJobTerminal(previous.status)) {
          upsertJob(
            { ...previous, jobId, status: "expired" },
            { label: previous?.label },
          );
        }
        removeTrackedJobId(jobId);
        continue;
      }

      upsertJob(status, { label: previous?.label });

      const latest = jobsRef.current[jobId];
      if (
        status.status === "completed" &&
        !latest?.acknowledged &&
        !isCompletionDismissed(jobId)
      ) {
        await handleJobCompleted(jobId, status);
      }

      if (isGenerationJobTerminal(status.status)) {
        removeTrackedJobId(jobId);
      }
    }
  }, [handleJobCompleted, isCompletionDismissed, upsertJob]);

  useEffect(() => {
    if (!generationJobTracker.isEnabled()) {
      return undefined;
    }

    void pollJobs();
    pollTimerRef.current = window.setInterval(() => {
      void pollJobs();
    }, generationJobTracker.pollIntervalMs);

    return () => {
      if (pollTimerRef.current) {
        window.clearInterval(pollTimerRef.current);
      }
    };
  }, [pollJobs]);

  const registerGenerationCancelHandler = useCallback((toolId, handler) => {
    if (!toolId || typeof handler !== "function") {
      return () => {};
    }

    generationCancelHandlersRef.current[toolId] = handler;
    return () => {
      if (generationCancelHandlersRef.current[toolId] === handler) {
        delete generationCancelHandlersRef.current[toolId];
      }
    };
  }, []);

  const cancelJob = useCallback(async (jobId) => {
    const existingJob = jobsRef.current[jobId];
    await generationJobTracker.cancel(jobId);
    const status = await generationJobTracker.getStatus(jobId);
    if (status) {
      upsertJob(status);
    }
    removeTrackedJobId(jobId);
    const toolId = status?.toolId ?? existingJob?.toolId;
    generationCancelHandlersRef.current[toolId]?.(jobId);
  }, [upsertJob]);

  const updateSavePreference = useCallback(async (jobId, saveToLibrary) => {
    await generationJobTracker.updatePreferences(jobId, saveToLibrary);
    setJobs((current) => {
      const job = current[jobId];
      if (!job) {
        return current;
      }
      return {
        ...current,
        [jobId]: { ...job, saveToLibrary },
      };
    });
  }, []);

  const getJob = useCallback(
    (jobId) => (jobId ? jobs[jobId] ?? null : null),
    [jobs],
  );

  const getJobForTool = useCallback(
    (toolId) => {
      const values = Object.values(jobs).filter(
        (job) => job.toolId === toolId && !isGenerationJobTerminal(job.status),
      );
      return values.length > 0 ? values[values.length - 1] : null;
    },
    [jobs],
  );

  const activeJobs = useMemo(
    () => Object.values(jobs).filter((job) => !isGenerationJobTerminal(job.status)),
    [jobs],
  );

  const recentCompletedJobs = useMemo(
    () =>
      Object.values(jobs).filter(
        (job) => job.status === "completed" && !job.acknowledged,
      ),
    [jobs],
  );

  const value = useMemo(
    () => ({
      jobs,
      activeJobs,
      registerJob,
      cancelJob,
      registerGenerationCancelHandler,
      updateSavePreference,
      getJob,
      getJobForTool,
      acknowledgeCompletion,
      upsertJob,
    }),
    [
      jobs,
      activeJobs,
      registerJob,
      cancelJob,
      registerGenerationCancelHandler,
      updateSavePreference,
      getJob,
      getJobForTool,
      acknowledgeCompletion,
      upsertJob,
    ],
  );

  return (
    <GenerationJobContext.Provider value={value}>
      {children}
      <GenerationCompletionToast
        toast={completionToast}
        onDismiss={acknowledgeCompletion}
      />
    </GenerationJobContext.Provider>
  );
}

export function useGenerationJobs() {
  const context = useContext(GenerationJobContext);
  if (!context) {
    throw new Error("useGenerationJobs must be used within GenerationJobProvider");
  }
  return context;
}

export function useOptionalGenerationJobs() {
  return useContext(GenerationJobContext);
}
